import { useEffect, useState } from "react"

import * as api from "../lib/api"
import type { DetailAutomat, ReportStatus } from "../types"

type Props = {
  id: number
  onReport: (id: number, status: ReportStatus) => Promise<void>
  onClose: () => void
}

function formatAgo(ts: number | null): string {
  if (!ts) return "brak zgłoszeń"
  const nowSec = Math.floor(Date.now() / 1000)
  const diff = Math.max(0, nowSec - ts)
  if (diff < 60) return "przed chwilą"
  if (diff < 3600) return `${Math.floor(diff / 60)} min temu`
  if (diff < 86400) return `${Math.floor(diff / 3600)} h temu`
  return `${Math.floor(diff / 86400)} dni temu`
}

export function AutomatPopup({ id, onReport, onClose }: Props) {
  const [data, setData] = useState<DetailAutomat | null>(null)
  const [busy, setBusy] = useState<ReportStatus | null>(null)

  useEffect(() => {
    let alive = true
    api
      .getDetail(id)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null))
    return () => {
      alive = false
    }
  }, [id])

  const handle = async (status: ReportStatus) => {
    setBusy(status)
    try {
      await onReport(id, status)
      const fresh = await api.getDetail(id)
      setData(fresh)
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="sheet" role="dialog" aria-label="Szczegóły automatu">
      <div className="grip" />
      <button className="close" onClick={onClose} aria-label="Zamknij">
        ← Zamknij
      </button>
      {!data && <p>Ładowanie…</p>}
      {data && (
        <>
          <h2>
            {data.chain ?? "Automat"}
            <span className={`badge ${data.current_status}`}>
              {data.current_status === "ok" ? "Działa" : "Nie działa"}
            </span>
          </h2>
          {data.name && <p className="meta">{data.name}</p>}
          <p className="meta">
            Ostatnie zgłoszenie: {formatAgo(data.last_report_at)}
            {data.recent_reports.length > 0 && (
              <>
                {" "}
                · {data.recent_reports.length} zgłoszeń(-ia) w historii
              </>
            )}
          </p>
          <div className="actions">
            <button className="ok" onClick={() => handle("ok")} disabled={busy !== null}>
              {busy === "ok" ? "…" : "✅ Działa"}
            </button>
            <button className="broken" onClick={() => handle("broken")} disabled={busy !== null}>
              {busy === "broken" ? "…" : "❌ Nie działa"}
            </button>
          </div>
        </>
      )}
    </section>
  )
}
